import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus, FileText, Layers, ExternalLink, Trash2, Loader2, Sparkles, Eye,
  Globe, Copy, ArrowUp, ArrowDown, GripVertical, PencilLine, LayoutTemplate,
  Filter as FilterIcon, Users, Send, ChevronRight, BarChart3, Upload,
  Code2, FileCode, Check, X, Link2
} from "lucide-react";
import type { LandingPage, LandingPageSection, FunnelStep, LandingPageData } from "@shared/schema";

const SECTION_TYPES = [
  { type: "hero", label: "Hero", icon: LayoutTemplate },
  { type: "stats", label: "Stats", icon: BarChart3 },
  { type: "benefits", label: "Benefits", icon: Sparkles },
  { type: "features", label: "Features", icon: Layers },
  { type: "testimonials", label: "Testimonials", icon: Users },
  { type: "faq", label: "FAQ", icon: FileText },
  { type: "cta", label: "Call to Action", icon: Send },
  { type: "logo_strip", label: "Logo Strip", icon: Layers },
  { type: "contact_form", label: "Contact Form", icon: Send },
] as const;

const STEP_TYPES = [
  { type: "single_select", label: "Single Choice" },
  { type: "multi_select", label: "Multiple Choice" },
  { type: "text_input", label: "Text Input" },
  { type: "info_screen", label: "Info Screen" },
  { type: "contact_form", label: "Contact Form" },
] as const;

function ExternalLeadBridgeModal({ workspaceId, onClose, onImported }: {
  workspaceId: number;
  onClose: () => void;
  onImported: (pageId?: number) => void;
}) {
  const [tab, setTab] = useState<"embed" | "import">("embed");
  const [label, setLabel] = useState("My Website");
  const [generatedLabel, setGeneratedLabel] = useState("");
  const [copied, setCopied] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [html, setHtml] = useState("");
  const [importing, setImporting] = useState(false);
  const { toast } = useToast();

  const embedQuery = useQuery({
    queryKey: ["/api/workspaces", workspaceId, "embed-script", generatedLabel],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/embed-script?label=${encodeURIComponent(generatedLabel)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch embed script");
      return res.text();
    },
    enabled: !!generatedLabel,
  });

  const generateSnippet = () => {
    setGeneratedLabel(label);
  };

  const copySnippet = async () => {
    if (!embedQuery.data) return;
    await navigator.clipboard.writeText(embedQuery.data);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendTestLead = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const snippetText = embedQuery.data || "";
      const apiKeyMatch = snippetText.match(/DRPR_API_KEY=("[^"]+")/);
      const parsedKey = apiKeyMatch ? JSON.parse(apiKeyMatch[1]) : "";
      const res = await fetch("/api/public/leads/capture", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${parsedKey}` },
        body: JSON.stringify({
          email: `test-${Date.now()}@drpr-test.com`,
          name: "Test Lead (External Bridge)",
          source: `External Website: ${generatedLabel || label}`,
          custom_fields: { test: true, bridge_test: new Date().toISOString() },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTestResult({ ok: true, msg: `Lead ${data.status === "updated" ? "updated" : "created"} (ID: ${data.leadId})` });
      } else {
        setTestResult({ ok: false, msg: data.error || "Failed" });
      }
    } catch (e: any) {
      setTestResult({ ok: false, msg: e.message });
    } finally {
      setTestSending(false);
    }
  };

  const importHtml = async () => {
    if (!html.trim()) return;
    setImporting(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${workspaceId}/pages/import-html`, { html });
      const data = await res.json();
      if (data.success) {
        toast({ title: "Page imported", description: `Draft page created from HTML. Opening editor...` });
        onImported(data.pageId);
        onClose();
      } else {
        toast({ title: "Import failed", description: data.message, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal-external-lead-bridge">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background border border-foreground/10 rounded-xl shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-foreground/[0.06]">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-blue-400" />
            <h2 className="text-sm font-display font-bold text-foreground">Connect External Site</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-foreground/5 text-foreground/30 hover:text-foreground/60" data-testid="button-close-bridge-modal">
            <X size={16} />
          </button>
        </div>

        <div className="flex border-b border-foreground/[0.06]">
          <button
            className={`flex-1 px-4 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === "embed" ? "text-blue-400 border-b-2 border-blue-400 bg-blue-400/5" : "text-foreground/30 hover:text-foreground/50"}`}
            onClick={() => setTab("embed")}
            data-testid="tab-embed-script"
          >
            <Code2 size={14} />
            Embed Script
          </button>
          <button
            className={`flex-1 px-4 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === "import" ? "text-purple-400 border-b-2 border-purple-400 bg-purple-400/5" : "text-foreground/30 hover:text-foreground/50"}`}
            onClick={() => setTab("import")}
            data-testid="tab-import-html"
          >
            <FileCode size={14} />
            Import HTML
          </button>
        </div>

        <div className="p-5">
          {tab === "embed" && (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-foreground/40 mb-3">
                  Paste this script into your external website. It intercepts any form with the <code className="px-1 py-0.5 rounded bg-foreground/5 text-blue-400 text-[11px] font-mono">data-drpr-form</code> attribute and sends leads directly to Rogers.
                </p>
                <label className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider mb-1 block">Source Label</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && generateSnippet()}
                    className="flex-1 px-3 py-2 text-sm bg-foreground/[0.03] border border-foreground/[0.08] rounded-lg text-foreground placeholder:text-foreground/20 focus:outline-none focus:border-blue-500/30"
                    placeholder="e.g. My P2P Networking Site"
                    data-testid="input-source-label"
                  />
                  <Button size="sm" onClick={generateSnippet} disabled={!label.trim()} data-testid="button-generate-snippet">
                    <Code2 size={14} className="mr-1.5" />
                    Generate
                  </Button>
                </div>
                <p className="text-[10px] text-foreground/20 mt-1">Leads will appear with source "External Website: {label}"</p>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider mb-1 block">Embed Snippet</label>
                {!generatedLabel ? (
                  <div className="flex items-center justify-center py-8 rounded-lg bg-[#0d1117] border border-foreground/[0.06]">
                    <p className="text-xs text-foreground/20">Enter a source label and click Generate to create your snippet</p>
                  </div>
                ) : embedQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8 rounded-lg bg-[#0d1117] border border-foreground/[0.06]">
                    <Loader2 className="animate-spin text-blue-400" size={20} />
                  </div>
                ) : embedQuery.isError ? (
                  <div className="flex items-center justify-center py-8 rounded-lg bg-[#0d1117] border border-foreground/[0.06]">
                    <p className="text-xs text-red-400">Failed to generate snippet. Check your connection and try again.</p>
                  </div>
                ) : (
                  <div className="relative group">
                    <pre className="p-4 rounded-lg bg-[#0d1117] border border-foreground/[0.06] text-[11px] leading-relaxed font-mono text-emerald-400/80 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all" data-testid="text-embed-snippet">
                      {embedQuery.data}
                    </pre>
                    <button
                      onClick={copySnippet}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-foreground/10 hover:bg-foreground/20 text-foreground/40 hover:text-foreground/70 transition-all opacity-0 group-hover:opacity-100"
                      data-testid="button-copy-snippet"
                    >
                      {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendTestLead}
                  disabled={testSending || !generatedLabel || !embedQuery.data}
                  data-testid="button-send-test-lead"
                >
                  {testSending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
                  Send Test Lead
                </Button>
                {testResult && (
                  <span className={`text-xs ${testResult.ok ? "text-emerald-400" : "text-red-400"}`} data-testid="text-test-result">
                    {testResult.ok ? <Check size={12} className="inline mr-1" /> : null}
                    {testResult.msg}
                  </span>
                )}
              </div>

              <div className="rounded-lg bg-foreground/[0.02] border border-foreground/[0.06] p-3 mt-2">
                <h4 className="text-[11px] font-semibold text-foreground/40 mb-2">Quick Setup Guide</h4>
                <ol className="text-[11px] text-foreground/30 space-y-1.5 list-decimal list-inside">
                  <li>Copy the snippet above and paste it before <code className="text-blue-400/60 font-mono">&lt;/body&gt;</code> on your site</li>
                  <li>Add <code className="text-blue-400/60 font-mono">data-drpr-form</code> to any <code className="text-blue-400/60 font-mono">&lt;form&gt;</code> element you want to capture</li>
                  <li>Use <code className="text-blue-400/60 font-mono">data-drpr-form="book"</code> instead to have DrPR take the form and show "pick a time now" with your next open times (booking must be on in Settings)</li>
                  <li>Form fields named <code className="text-blue-400/60 font-mono">email</code>, <code className="text-blue-400/60 font-mono">name</code>, <code className="text-blue-400/60 font-mono">phone</code> are mapped automatically</li>
                  <li>All other fields are stored as custom fields on the lead</li>
                  <li>Rogers will begin nurturing new leads automatically</li>
                </ol>
              </div>
            </div>
          )}

          {tab === "import" && (
            <div className="space-y-4">
              <p className="text-xs text-foreground/40">
                Paste the HTML source of an external landing page. Gemini will analyze it and create an editable draft page with extracted sections, content, and theme colors.
              </p>
              <div>
                <label className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider mb-1 block">HTML Source Code</label>
                <textarea
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 text-[12px] font-mono bg-[#0d1117] border border-foreground/[0.08] rounded-lg text-foreground/70 placeholder:text-foreground/15 focus:outline-none focus:border-purple-500/30 resize-none"
                  placeholder="<!DOCTYPE html>\n<html>\n  <head>...</head>\n  <body>...</body>\n</html>"
                  data-testid="textarea-import-html"
                />
                <p className="text-[10px] text-foreground/20 mt-1">{html.length > 0 ? `${(html.length / 1024).toFixed(1)} KB` : "Max 500 KB"}</p>
              </div>
              <Button
                size="sm"
                onClick={importHtml}
                disabled={importing || !html.trim()}
                data-testid="button-import-html"
              >
                {importing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                {importing ? "Analyzing with Gemini..." : "Import Page"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PageList({ pages, onEdit, onCreate, onDelete, workspaceId, onRefresh, onEditById }: {
  pages: LandingPage[];
  onEdit: (p: LandingPage) => void;
  onCreate: (type: string) => void;
  onDelete: (id: number) => void;
  workspaceId: number;
  onRefresh: () => void;
  onEditById: (pageId: number) => void;
}) {
  const [showBridgeModal, setShowBridgeModal] = useState(false);
  return (
    <div className="space-y-6">
      {showBridgeModal && (
        <ExternalLeadBridgeModal
          workspaceId={workspaceId}
          onClose={() => setShowBridgeModal(false)}
          onImported={(pageId) => {
            onRefresh();
            if (pageId) onEditById(pageId);
          }}
        />
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-display font-bold text-foreground tracking-tight" data-testid="text-pages-title">Pages</h1>
          <p className="text-xs text-foreground/30 mt-1">Build landing pages and lead funnels. Peggy generates content from your strategy.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowBridgeModal(true)} data-testid="button-connect-external">
            <Link2 size={14} className="mr-1.5" />
            Connect External Site
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCreate("funnel")} data-testid="button-create-funnel">
            <FilterIcon size={14} className="mr-1.5" />
            New Funnel
          </Button>
          <Button size="sm" onClick={() => onCreate("marketing")} data-testid="button-create-page">
            <Plus size={14} className="mr-1.5" />
            New Landing Page
          </Button>
        </div>
      </div>

      {pages.length === 0 ? (
        <Card className="p-12 text-center bg-foreground/[0.02] border-foreground/[0.06]">
          <LayoutTemplate size={40} className="mx-auto text-foreground/10 mb-4" />
          <h3 className="text-sm font-semibold text-foreground/40 mb-1">No pages yet</h3>
          <p className="text-xs text-foreground/20 mb-4">Create a landing page or lead funnel to start capturing leads.</p>
          <div className="flex gap-2 justify-center">
            <Button size="sm" variant="outline" onClick={() => onCreate("funnel")} data-testid="button-create-funnel-empty">
              <FilterIcon size={14} className="mr-1.5" />
              Lead Funnel
            </Button>
            <Button size="sm" onClick={() => onCreate("marketing")} data-testid="button-create-page-empty">
              <Plus size={14} className="mr-1.5" />
              Landing Page
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <Card
              key={page.id}
              className="bg-foreground/[0.02] border-foreground/[0.06] p-4 space-y-3 hover-elevate cursor-pointer"
              onClick={() => onEdit(page)}
              data-testid={`page-card-${page.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {page.type === "funnel" ? <FilterIcon size={16} className="text-purple-400" /> : <LayoutTemplate size={16} className="text-blue-400" />}
                  <h3 className="text-sm font-semibold text-foreground truncate">{page.title}</h3>
                </div>
                <Badge variant={page.status === "published" ? "default" : "secondary"} className={`text-[10px] ${page.status === "published" ? "bg-emerald-600/20 text-emerald-400" : ""}`}>
                  {page.status === "published" ? "Live" : "Draft"}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-foreground/30">
                <span>{page.type === "funnel" ? "Lead Funnel" : "Landing Page"}</span>
                <span>/p/{page.slug}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground/20">
                <span><Eye size={12} className="inline mr-1" />{page.views} views</span>
                <span><Send size={12} className="inline mr-1" />{page.submissions} leads</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); const url = page.status === "published" ? `/p/${page.slug}` : `/p/${page.slug}?preview=1&previewId=${page.id}&wsId=${page.workspaceId}`; window.open(url, "_blank"); }} data-testid={`button-preview-${page.id}`}>
                  <ExternalLink size={12} className="mr-1" />
                  Preview
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7 text-red-400" onClick={(e) => { e.stopPropagation(); onDelete(page.id); }} data-testid={`button-delete-${page.id}`}>
                  <Trash2 size={12} className="mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionEditor({ section, onChange, onRemove, onMoveUp, onMoveDown, workspaceId }: {
  section: LandingPageSection;
  onChange: (s: LandingPageSection) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  workspaceId?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const sectionDef = SECTION_TYPES.find(s => s.type === section.type);

  return (
    <div className="rounded-lg border border-foreground/[0.06] bg-foreground/[0.02]" data-testid={`section-editor-${section.id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={14} className="text-foreground/15" />
        <span className="text-xs font-semibold text-foreground/50 uppercase w-20">{sectionDef?.label || section.type}</span>
        <span className="text-xs text-foreground/30 flex-1 truncate">{section.title || "Untitled"}</span>
        <div className="flex items-center gap-1">
          {onMoveUp && <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1 rounded opacity-30 hover:opacity-60"><ArrowUp size={12} /></button>}
          {onMoveDown && <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1 rounded opacity-30 hover:opacity-60"><ArrowDown size={12} /></button>}
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1 rounded text-red-400/30 hover:text-red-400/60"><Trash2 size={12} /></button>
          <ChevronRight size={14} className={`text-foreground/20 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-foreground/[0.04] pt-3">
          {(section.title || section.subtitle) && (
            <div className="rounded-md border border-foreground/[0.06] bg-foreground/[0.015] px-3 py-2 mb-1" data-testid={`preview-section-${section.id}`}>
              {section.title && <div className="text-sm font-bold text-foreground/70 truncate">{section.title}</div>}
              {section.subtitle && <div className="text-[11px] text-foreground/30 mt-0.5 truncate">{section.subtitle}</div>}
              {section.backgroundImage && <div className="text-[9px] text-foreground/15 mt-1 flex items-center gap-1"><Eye size={9} /> bg image set</div>}
            </div>
          )}
          <div>
            <label className="text-[10px] text-foreground/30 uppercase font-semibold">Title</label>
            <input
              value={section.title || ""}
              onChange={(e) => onChange({ ...section, title: e.target.value })}
              className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
              data-testid={`input-section-title-${section.id}`}
            />
          </div>
          {(section.type === "hero" || section.type === "cta" || section.type === "contact_form" || section.type === "benefits") && (
            <div>
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Subtitle</label>
              <input
                value={section.subtitle || ""}
                onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
                className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                data-testid={`input-section-subtitle-${section.id}`}
              />
            </div>
          )}
          {(section.type === "hero" || section.type === "cta") && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-foreground/30 uppercase font-semibold">CTA Text</label>
                  <input
                    value={section.ctaText || ""}
                    onChange={(e) => onChange({ ...section, ctaText: e.target.value })}
                    className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                    data-testid={`input-section-cta-${section.id}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground/30 uppercase font-semibold">CTA URL</label>
                  <input
                    value={section.ctaUrl || ""}
                    onChange={(e) => onChange({ ...section, ctaUrl: e.target.value })}
                    className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                    data-testid={`input-section-ctaurl-${section.id}`}
                  />
                </div>
              </div>
            </>
          )}
          {["hero", "benefits", "features", "cta", "testimonials", "stats"].includes(section.type) && (
            <div>
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Background Image</label>
              <div className="flex gap-2 mt-1">
                <input
                  value={section.backgroundImage || ""}
                  onChange={(e) => onChange({ ...section, backgroundImage: e.target.value })}
                  placeholder="https://example.com/hero-bg.jpg"
                  className="flex-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                  data-testid={`input-section-bgimage-${section.id}`}
                />
                <label className="flex items-center gap-1 px-2 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground/40 hover:text-foreground/60 cursor-pointer text-[10px] transition-colors" data-testid={`button-upload-bgimage-${section.id}`}>
                  <Upload size={12} />
                  <span>Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = async () => {
                      const base64 = (reader.result as string).split(",")[1];
                      try {
                        if (!workspaceId) return;
                        const resp = await apiRequest("POST", `/api/workspaces/${workspaceId}/uploads`, {
                          data: base64, filename: file.name, mimeType: file.type,
                        });
                        const result = await resp.json();
                        if (result.url) onChange({ ...section, backgroundImage: result.url });
                      } catch (err) { console.error("Upload failed:", err); }
                    };
                    reader.readAsDataURL(file);
                  }} />
                </label>
              </div>
              <p className="text-[9px] text-foreground/20 mt-1">Paste a URL or upload an image. Leave empty for gradient.</p>
            </div>
          )}
          {section.type === "contact_form" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-foreground/30 uppercase font-semibold">Button Text</label>
                <input
                  value={section.ctaText || ""}
                  onChange={(e) => onChange({ ...section, ctaText: e.target.value })}
                  placeholder="Get in Touch"
                  className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                  data-testid="input-section-contact-button-text"
                />
              </div>
            </div>
          )}
          {section.items && (
            <div className="space-y-2">
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Items ({section.items.length})</label>
              {section.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1 space-y-1.5">
                    <input
                      value={item.title || item.question || item.name || item.stat || ""}
                      onChange={(e) => {
                        const items = [...(section.items || [])];
                        const key = item.question ? "question" : item.name ? "name" : item.stat ? "stat" : "title";
                        items[i] = { ...items[i], [key]: e.target.value };
                        onChange({ ...section, items });
                      }}
                      placeholder="Title"
                      className="w-full px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                    />
                    <input
                      value={item.description || item.answer || item.quote || item.label || ""}
                      onChange={(e) => {
                        const items = [...(section.items || [])];
                        const key = item.answer ? "answer" : item.quote ? "quote" : item.label ? "label" : "description";
                        items[i] = { ...items[i], [key]: e.target.value };
                        onChange({ ...section, items });
                      }}
                      placeholder="Description"
                      className="w-full px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                    />
                    {(section.type === "logo_strip" || section.type === "testimonials") && (
                      <input
                        value={item.imageUrl || ""}
                        onChange={(e) => {
                          const items = [...(section.items || [])];
                          items[i] = { ...items[i], imageUrl: e.target.value };
                          onChange({ ...section, items });
                        }}
                        placeholder={section.type === "logo_strip" ? "Logo image URL" : "Avatar URL (optional)"}
                        className="w-full px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                        data-testid={`input-item-imageurl-${i}`}
                      />
                    )}
                  </div>
                  <button onClick={() => {
                    const items = (section.items || []).filter((_, idx) => idx !== i);
                    onChange({ ...section, items });
                  }} className="p-1 text-red-400/30 hover:text-red-400/60 mt-1"><Trash2 size={10} /></button>
                </div>
              ))}
              <button
                onClick={() => {
                  const items = [...(section.items || []), { title: "New item", description: "" }];
                  onChange({ ...section, items });
                }}
                className="text-[10px] text-blue-400/60 hover:text-blue-400 flex items-center gap-1"
                data-testid={`button-add-item-${section.id}`}
              >
                <Plus size={10} /> Add item
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepEditor({ step, onChange, onRemove, onMoveUp, onMoveDown }: {
  step: FunnelStep;
  onChange: (s: FunnelStep) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const typeDef = STEP_TYPES.find(s => s.type === step.type);

  return (
    <div className="rounded-lg border border-foreground/[0.06] bg-foreground/[0.02]" data-testid={`step-editor-${step.id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={14} className="text-foreground/15" />
        <span className="text-xs font-semibold text-purple-400/60 uppercase w-24">{typeDef?.label || step.type}</span>
        <span className="text-xs text-foreground/30 flex-1 truncate">{step.title || "Untitled"}</span>
        <div className="flex items-center gap-1">
          {onMoveUp && <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1 rounded opacity-30 hover:opacity-60"><ArrowUp size={12} /></button>}
          {onMoveDown && <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1 rounded opacity-30 hover:opacity-60"><ArrowDown size={12} /></button>}
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1 rounded text-red-400/30 hover:text-red-400/60"><Trash2 size={12} /></button>
          <ChevronRight size={14} className={`text-foreground/20 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-foreground/[0.04] pt-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Type</label>
              <select
                value={step.type}
                onChange={(e) => onChange({ ...step, type: e.target.value as any })}
                className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
              >
                {STEP_TYPES.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">CTA Text</label>
              <input
                value={step.ctaText || ""}
                onChange={(e) => onChange({ ...step, ctaText: e.target.value })}
                className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-foreground/30 uppercase font-semibold">Title</label>
            <input
              value={step.title || ""}
              onChange={(e) => onChange({ ...step, title: e.target.value })}
              className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
              data-testid={`input-step-title-${step.id}`}
            />
          </div>
          <div>
            <label className="text-[10px] text-foreground/30 uppercase font-semibold">Subtitle</label>
            <input
              value={step.subtitle || ""}
              onChange={(e) => onChange({ ...step, subtitle: e.target.value })}
              className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
            />
          </div>
          {(step.type === "single_select" || step.type === "multi_select") && (
            <div className="space-y-2">
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Options ({step.options?.length || 0})</label>
              {step.options?.map((opt, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={opt.label}
                    onChange={(e) => {
                      const options = [...(step.options || [])];
                      options[i] = { ...options[i], label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, "_") };
                      onChange({ ...step, options });
                    }}
                    className="flex-1 px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                  />
                  <button onClick={() => {
                    const options = (step.options || []).filter((_, idx) => idx !== i);
                    onChange({ ...step, options });
                  }} className="p-1 text-red-400/30 hover:text-red-400/60"><Trash2 size={10} /></button>
                </div>
              ))}
              <button
                onClick={() => {
                  const options = [...(step.options || []), { label: "New Option", value: `opt_${Date.now()}` }];
                  onChange({ ...step, options });
                }}
                className="text-[10px] text-blue-400/60 hover:text-blue-400 flex items-center gap-1"
              >
                <Plus size={10} /> Add option
              </button>
            </div>
          )}
          {step.type === "contact_form" && (
            <div className="space-y-2">
              <label className="text-[10px] text-foreground/30 uppercase font-semibold">Fields ({step.fields?.length || 0})</label>
              {step.fields?.map((field, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={field.label}
                    onChange={(e) => {
                      const fields = [...(step.fields || [])];
                      fields[i] = { ...fields[i], label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, "_") };
                      onChange({ ...step, fields });
                    }}
                    className="flex-1 px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                  />
                  <select
                    value={field.type}
                    onChange={(e) => {
                      const fields = [...(step.fields || [])];
                      fields[i] = { ...fields[i], type: e.target.value as any };
                      onChange({ ...step, fields });
                    }}
                    className="px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px]"
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="textarea">Textarea</option>
                  </select>
                  <button onClick={() => {
                    const fields = (step.fields || []).filter((_, idx) => idx !== i);
                    onChange({ ...step, fields });
                  }} className="p-1 text-red-400/30 hover:text-red-400/60"><Trash2 size={10} /></button>
                </div>
              ))}
              <button
                onClick={() => {
                  const fields = [...(step.fields || []), { name: `field_${Date.now()}`, label: "New Field", type: "text" as const, required: false }];
                  onChange({ ...step, fields });
                }}
                className="text-[10px] text-blue-400/60 hover:text-blue-400 flex items-center gap-1"
              >
                <Plus size={10} /> Add field
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PageEditor({ page, onBack }: { page: LandingPage; onBack: () => void }) {
  const { toast } = useToast();
  const wsId = page.workspaceId;
  const [pageData, setPageData] = useState<LandingPageData>(page.pageData as LandingPageData || {});
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");

  const saveMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/pages/${page.id}`, { title, slug, pageData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "pages"] });
      toast({ title: "Saved", description: "Page saved successfully." });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const publishMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/pages/${page.id}/${page.status === "published" ? "unpublish" : "publish"}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "pages"] });
      toast({ title: page.status === "published" ? "Unpublished" : "Published" });
    },
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`/api/workspaces/${wsId}/pages/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: page.type, prompt: aiPrompt }),
      });
      if (!res.ok) throw new Error("Generation failed");
      const data = await res.json();
      if (page.type === "funnel" && data.steps) {
        setPageData({ ...pageData, steps: data.steps, theme: data.theme || pageData.theme });
      } else if (data.sections) {
        setPageData({ ...pageData, sections: data.sections, theme: data.theme || pageData.theme });
      }
      toast({ title: "Generated", description: "Peggy created your page content." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
    setGenerating(false);
  };

  const sections = pageData.sections || [];
  const steps = pageData.steps || [];
  const isFunnel = page.type === "funnel";

  const moveSectionUp = (i: number) => {
    if (i <= 0) return;
    const arr = [...sections];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setPageData({ ...pageData, sections: arr });
  };
  const moveSectionDown = (i: number) => {
    if (i >= sections.length - 1) return;
    const arr = [...sections];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setPageData({ ...pageData, sections: arr });
  };
  const moveStepUp = (i: number) => {
    if (i <= 0) return;
    const arr = [...steps];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setPageData({ ...pageData, steps: arr });
  };
  const moveStepDown = (i: number) => {
    if (i >= steps.length - 1) return;
    const arr = [...steps];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setPageData({ ...pageData, steps: arr });
  };

  const previewUrl = page.status === "published"
    ? `/p/${page.slug}`
    : `/p/${page.slug}?preview=1&previewId=${page.id}&wsId=${page.workspaceId}`;

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      await apiRequest("PATCH", `/api/pages/${page.id}`, { title, slug, pageData });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "pages"] });
    } catch {}
    setPreviewSrc(previewUrl + (previewUrl.includes("?") ? "&" : "?") + "_t=" + Date.now());
    setShowPreview(true);
    setPreviewing(false);
  };

  if (showPreview) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col bg-background" data-testid="page-preview-overlay">
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-foreground/[0.08] bg-foreground/[0.02] shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground/40 font-medium uppercase tracking-wide">Preview</span>
            <Badge variant="secondary" className="text-[10px]">{title}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-foreground/30 font-mono truncate max-w-xs hidden sm:block">{previewUrl}</span>
            <Button size="sm" variant="ghost" className="text-xs h-7 gap-1.5" onClick={() => { window.open(previewSrc, "_blank"); }} data-testid="button-preview-open-tab">
              <ExternalLink size={12} /> Open in tab
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowPreview(false)} data-testid="button-close-preview">
              ✕ Close preview
            </Button>
          </div>
        </div>
        <iframe
          src={previewSrc}
          className="flex-1 w-full border-0"
          title="Page Preview"
          data-testid="iframe-page-preview"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="page-editor">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-foreground/30 hover:text-foreground/60 text-xs" data-testid="button-back-to-list">
            &larr; Back
          </button>
          <h1 className="text-lg font-display font-bold text-foreground" data-testid="text-editor-title">{title}</h1>
          <Badge variant="secondary" className={`text-[10px] ${page.status === "published" ? "bg-emerald-600/20 text-emerald-400" : ""}`}>
            {page.status === "published" ? "Live" : "Draft"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing} data-testid="button-open-preview">
            {previewing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ExternalLink size={14} className="mr-1.5" />}
            Preview
          </Button>
          <Button size="sm" variant="outline" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending} data-testid="button-publish-toggle">
            <Globe size={14} className="mr-1.5" />
            {page.status === "published" ? "Unpublish" : "Publish"}
          </Button>
          <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-page">
            {saveMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <PencilLine size={14} className="mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4 bg-foreground/[0.02] border-foreground/[0.06] space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-foreground/30 uppercase font-semibold">Page Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                  data-testid="input-page-title"
                />
              </div>
              <div>
                <label className="text-[10px] text-foreground/30 uppercase font-semibold">URL Slug</label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[10px] text-foreground/20">/p/</span>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    className="flex-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                    data-testid="input-page-slug"
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`); toast({ title: "Copied!" }); }}
                    className="p-1.5 rounded-md text-foreground/20 hover:text-foreground/50"
                    data-testid="button-copy-url"
                  >
                    <Copy size={12} />
                  </button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-foreground/[0.02] border-foreground/[0.06] space-y-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h3 className="text-xs font-semibold text-foreground/40 uppercase">{isFunnel ? "Funnel Steps" : "Page Sections"}</h3>
              {isFunnel ? (
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => {
                  const newStep: FunnelStep = { id: `step_${Date.now()}`, type: "single_select", title: "New Question", options: [{ label: "Option 1", value: "opt_1" }], ctaText: "Continue" };
                  setPageData({ ...pageData, steps: [...steps, newStep] });
                }} data-testid="button-add-step">
                  <Plus size={12} className="mr-1" /> Add Step
                </Button>
              ) : (
                <div className="flex gap-1 flex-wrap">
                  {SECTION_TYPES.map(st => (
                    <button
                      key={st.type}
                      onClick={() => {
                        const newSection: LandingPageSection = {
                          id: `${st.type}_${Date.now()}`,
                          type: st.type as LandingPageSection['type'],
                          title: st.type === "contact_form" ? "Get in Touch" : st.type === "logo_strip" ? "Trusted By" : st.label,
                          subtitle: st.type === "contact_form" ? "Fill in the form below and we'll get back to you." : undefined,
                          ctaText: st.type === "contact_form" ? "Send Message" : undefined,
                          items: ["benefits", "features", "testimonials", "faq", "stats", "logo_strip"].includes(st.type)
                            ? [{ title: "Item 1", description: "Description" }]
                            : undefined,
                        };
                        setPageData({ ...pageData, sections: [...sections, newSection] });
                      }}
                      className="text-[10px] px-2 py-1 rounded-md text-foreground/30 hover:text-foreground/60 hover:bg-foreground/[0.04] transition-colors"
                      data-testid={`button-add-section-${st.type}`}
                    >
                      + {st.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {isFunnel ? (
                steps.length === 0 ? (
                  <p className="text-xs text-foreground/20 text-center py-4">No steps yet. Add steps or let Peggy generate them.</p>
                ) : (
                  steps.map((step, i) => (
                    <StepEditor
                      key={step.id}
                      step={step}
                      onChange={(s) => {
                        const arr = [...steps];
                        arr[i] = s;
                        setPageData({ ...pageData, steps: arr });
                      }}
                      onRemove={() => setPageData({ ...pageData, steps: steps.filter((_, idx) => idx !== i) })}
                      onMoveUp={i > 0 ? () => moveStepUp(i) : undefined}
                      onMoveDown={i < steps.length - 1 ? () => moveStepDown(i) : undefined}
                    />
                  ))
                )
              ) : (
                sections.length === 0 ? (
                  <p className="text-xs text-foreground/20 text-center py-4">No sections yet. Add sections or let Peggy generate them.</p>
                ) : (
                  sections.map((section, i) => (
                    <SectionEditor
                      key={section.id}
                      section={section}
                      workspaceId={wsId}
                      onChange={(s) => {
                        const arr = [...sections];
                        arr[i] = s;
                        setPageData({ ...pageData, sections: arr });
                      }}
                      onRemove={() => setPageData({ ...pageData, sections: sections.filter((_, idx) => idx !== i) })}
                      onMoveUp={i > 0 ? () => moveSectionUp(i) : undefined}
                      onMoveDown={i < sections.length - 1 ? () => moveSectionDown(i) : undefined}
                    />
                  ))
                )
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-4 bg-foreground/[0.02] border-foreground/[0.06] space-y-3">
            <h3 className="text-xs font-semibold text-foreground/40 uppercase flex items-center gap-1.5">
              <Sparkles size={12} className="text-purple-400" />
              AI Generate (Peggy)
            </h3>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={isFunnel ? "e.g., Create a lead qualification funnel for our consulting services..." : "e.g., Build a page highlighting our premium features..."}
              className="w-full px-3 py-2 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground placeholder:text-foreground/20 text-xs resize-none min-h-[80px] focus:border-blue-500/50 focus:outline-none"
              data-testid="input-ai-prompt"
            />
            <Button size="sm" className="w-full" onClick={handleGenerate} disabled={generating} data-testid="button-ai-generate">
              {generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              {generating ? "Peggy is working..." : `Generate ${isFunnel ? "Funnel" : "Page"}`}
            </Button>
            <p className="text-[10px] text-foreground/20">Peggy uses your marketing strategy to create relevant content.</p>
          </Card>

          <Card className="p-4 bg-foreground/[0.02] border-foreground/[0.06] space-y-3">
            <h3 className="text-xs font-semibold text-foreground/40 uppercase">Theme</h3>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-foreground/30">Brand Name</label>
                <input
                  value={pageData.theme?.brandName || ""}
                  onChange={(e) => setPageData({ ...pageData, theme: { ...pageData.theme, brandName: e.target.value } })}
                  className="w-full mt-1 px-3 py-1.5 rounded-md border border-foreground/10 bg-foreground/[0.03] text-foreground text-xs focus:border-blue-500/50 focus:outline-none"
                  data-testid="input-theme-brandname"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { key: "primaryColor" as const, label: "Primary Color", def: "#3B82F6" },
                    { key: "backgroundColor" as const, label: "Background", def: "#0A0A0F" },
                    { key: "textColor" as const, label: "Text Color", def: "#FFFFFF" },
                    { key: "buttonColor" as const, label: "Button Color", def: "#3B82F6" },
                    { key: "cardBackground" as const, label: "Card Background", def: "" },
                  ] as const
                ).map((c) => (
                  <div key={c.key}>
                    <label className="text-[10px] text-foreground/30">{c.label}</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={pageData.theme?.[c.key] || c.def || "#1A1A2E"}
                        onChange={(e) => setPageData({ ...pageData, theme: { ...pageData.theme, [c.key]: e.target.value } })}
                        className="w-7 h-7 rounded border border-foreground/10 cursor-pointer"
                        data-testid={`input-theme-${c.key}-picker`}
                      />
                      <input
                        value={pageData.theme?.[c.key] || c.def}
                        onChange={(e) => setPageData({ ...pageData, theme: { ...pageData.theme, [c.key]: e.target.value } })}
                        placeholder={c.def || "Auto from background"}
                        className="flex-1 px-2 py-1 rounded border border-foreground/10 bg-foreground/[0.03] text-foreground text-[11px] focus:border-blue-500/50 focus:outline-none"
                        data-testid={`input-theme-${c.key}-text`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-foreground/[0.02] border-foreground/[0.06] space-y-2">
            <h3 className="text-xs font-semibold text-foreground/40 uppercase">Stats</h3>
            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground/30">Views</span>
              <span className="text-foreground/60 font-mono">{page.views}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground/30">Submissions</span>
              <span className="text-foreground/60 font-mono">{page.submissions}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground/30">Conversion</span>
              <span className="text-foreground/60 font-mono">{page.views > 0 ? ((page.submissions / page.views) * 100).toFixed(1) : "0"}%</span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export function PageBuilderEmbed() {
  return <PageBuilder embedded />;
}

export default function PageBuilder({ embedded = false }: { embedded?: boolean } = {}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [editingPage, setEditingPage] = useState<LandingPage | null>(null);

  const { data: pages, isLoading } = useQuery<LandingPage[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "pages"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/pages`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  useEffect(() => {
    if (!pages || pages.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("editPage");
    if (editId && !editingPage) {
      const target = pages.find(p => String(p.id) === editId);
      if (target) {
        setEditingPage(target);
        const url = new URL(window.location.href);
        url.searchParams.delete("editPage");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [pages, editingPage]);

  const createMutation = useMutation({
    mutationFn: async (type: string) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/pages`, {
        title: type === "funnel" ? "New Lead Funnel" : "New Landing Page",
        type,
      });
      return res.json();
    },
    onSuccess: (page: LandingPage) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "pages"] });
      setEditingPage(page);
      toast({ title: "Created", description: `${page.type === "funnel" ? "Lead funnel" : "Landing page"} created.` });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/pages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "pages"] });
      toast({ title: "Deleted" });
    },
  });

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-foreground/30 text-sm">Select a workspace first.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (editingPage) {
    const freshPage = pages?.find(p => p.id === editingPage.id) || editingPage;
    const content = <PageEditor page={freshPage} onBack={() => setEditingPage(null)} />;
    return embedded ? content : <div className="p-6 max-w-7xl mx-auto">{content}</div>;
  }

  const content = (
    <PageList
      pages={pages || []}
      onEdit={setEditingPage}
      onCreate={(type) => createMutation.mutate(type)}
      onDelete={(id) => deleteMutation.mutate(id)}
      workspaceId={activeWorkspaceId}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "pages"] })}
      onEditById={async (pageId) => {
        await queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "pages"] });
        const freshPages = queryClient.getQueryData<LandingPage[]>(["/api/workspaces", activeWorkspaceId, "pages"]);
        const target = freshPages?.find(p => p.id === pageId);
        if (target) setEditingPage(target);
        else {
          try {
            const res = await fetch(`/api/workspaces/${activeWorkspaceId}/pages/${pageId}/preview-data`, { credentials: "include" });
            if (res.ok) {
              const data = await res.json();
              if (data.page) setEditingPage(data.page);
            }
          } catch {}
        }
      }}
    />
  );
  return embedded ? content : <div className="p-6 max-w-7xl mx-auto">{content}</div>;
}